import { useSelector } from 'react-redux';
import { filter, find, sum, sumBy } from 'lodash';

import { getSelectedRidgeCap } from '@/components/modals/SubmitOrder/components/OrderDetailsStep/components/EquipmentDetails/constants';
import {
  getOrderItemPrice,
  isAsphaltShingle,
  isEquipment,
  isInventory,
  isService,
  isStorageProduct,
  isWarranty,
} from '@/components/modals/SubmitOrder/constants';
import { State } from '@/components/modals/SubmitOrder/types';
import { ProposalWithBom } from '@/controllers/Activity';
import { selectProposalItems } from '@/store/reducers/dictionaries/selectors';
import { selectNewOpportunity } from '@/store/reducers/opportunities/selectors';
import { selectDesignToOrderProducts } from '@/store/reducers/products/selectors';

export const useBomTotal = (orderContext: State, proposal: ProposalWithBom | null): number => {
  const oppty = useSelector(selectNewOpportunity);
  const allPriceBookEntries = useSelector(selectDesignToOrderProducts);
  const proposalItems = useSelector(selectProposalItems) ?? [];

  if (orderContext.isDistribution || !proposal) {
    // distro proposal do not show BOM or components
    return 0;
  }

  const bomCost = proposal.estimatedSolarMaterialsCost;
  const selectedEquipment = filter(
    orderContext.visibleOrderItems,
    oi => isEquipment(oi) && !isInventory(oi),
  );

  // shingle HDZ cost
  const selectedAsphaltShingle = find(selectedEquipment, e => isAsphaltShingle(e)) ?? null;
  const asphaltCost = selectedAsphaltShingle
    ? getOrderItemPrice(orderContext, selectedAsphaltShingle)
    : 0;

  // services
  const services = filter(orderContext.visibleOrderItems, oi => isService(oi));
  const servicesCost = sumBy(services, service => getOrderItemPrice(orderContext, service));

  // ridge caps
  const priceBookEntries = oppty
    ? filter(allPriceBookEntries, pbe => pbe.priceBookId === oppty.priceBookId)
    : allPriceBookEntries;

  const selectedRidgeCap = getSelectedRidgeCap(
    selectedAsphaltShingle,
    selectedEquipment,
    priceBookEntries,
    proposalItems,
  );
  const ridgeCapCost = selectedRidgeCap ? getOrderItemPrice(orderContext, selectedRidgeCap) : 0;

  // Energy storage, batteries selection
  const storageItem = orderContext.visibleOrderItems.filter(oi =>
    isStorageProduct(oi.priceBookEntry),
  );
  const storageCost = storageItem.map(oi => getOrderItemPrice(orderContext, oi));
  const totalStorageCost = sum(storageCost);

  const warranties = filter(orderContext.visibleOrderItems, oi => isWarranty(oi.priceBookEntry));
  const warrantiesTotal = sumBy(warranties, oi => getOrderItemPrice(orderContext, oi, proposal));

  return sum([bomCost, asphaltCost, servicesCost, ridgeCapCost, totalStorageCost, warrantiesTotal]);
};
